import { LuIcon, Text } from "@overmap-ai/blocks"
import { EMPTY_ARRAY } from "@overmap-ai/common"
import type { Project } from "@overmap-ai/common"
import { use, useCallback, useMemo } from "react"

import { ProjectMultiSelectDropdown } from "../../../components/DropdownMultiSelects/ProjectMultiSelectDropdown"
import { ProjectSelect } from "../../../components/DropdownSelects/ProjectSelect"
import { modelIconMapping } from "../../../mappings/icons"
import { FilterModifierDropdown, RemoveFilterButton } from "../../components"
import { FilterCellBadge } from "../../components/FilterCellBadge"
import { FilterCellButton } from "../../components/FilterCellButton"
import { FilterCellRoot } from "../../components/FilterCellRoot"
import { FilterRendererContext } from "../../contexts/FilterRendererContext/context"
import type { FilterDefRenderProps } from "../BaseFilterDef"
import type { ProjectFilterDef } from "./ProjectFilterDef"

type ProjectFilterCellProps = FilterDefRenderProps<ProjectFilterDef>

export const ProjectFilterCell = (props: ProjectFilterCellProps) => {
	const { filterDef, filter, filterManager } = props
	const { size } = use(FilterRendererContext)
	const projects = filterDef.projects
	const filterValue = filter.getFilterValue()
	const filterModifier = filter.getFilterModifier()

	const selectedProject = useMemo(() => {
		if (filterValue === undefined || !filterDef.modifiers.equals.isFilterValueValid(filterValue)) return undefined
		return projects.find(({ uuid }) => uuid === filterValue)
	}, [filterDef.modifiers.equals, filterValue, projects])

	const selectedProjects = useMemo(() => {
		if (filterValue === undefined || !filterDef.modifiers.includes.isFilterValueValid(filterValue)) return undefined

		const filterValueSet = new Set(filterValue)
		return projects.filter(({ uuid }) => filterValueSet.has(uuid))
	}, [filterDef.modifiers.includes, filterValue, projects])

	const handleValueSelect = useCallback(
		(project?: Project) => {
			filter.setFilterValue(project?.uuid)
		},
		[filter],
	)

	const handleValuesChange = useCallback(
		(projects: Project[]) => {
			filter.setFilterValue(projects.length > 0 ? projects.map(({ uuid }) => uuid) : undefined)
		},
		[filter],
	)

	const getFilterValueUi = () => {
		switch (filterModifier) {
			case "equals":
			case "notEquals":
				return (
					<ProjectSelect
						projects={projects}
						required={false}
						value={selectedProject}
						onValueChange={handleValueSelect}
						size={size}
					>
						<FilterCellButton className="shrink-1 min-w-0 grow">
							{selectedProject ? <Text className="truncate">{selectedProject.name}</Text> : "..."}
						</FilterCellButton>
					</ProjectSelect>
				)
			case "includes":
			case "excludes": {
				const getLabelUi = () => {
					switch ((selectedProjects ?? EMPTY_ARRAY).length) {
						case 0:
							return "..."
						case 1:
							return <Text className="truncate">{selectedProjects![0].name}</Text>
						default:
							return <Text className="truncate">{`${selectedProjects!.length} projects`}</Text>
					}
				}

				return (
					<ProjectMultiSelectDropdown
						projects={projects}
						values={selectedProjects}
						onValuesChange={handleValuesChange}
						size={size}
					>
						<FilterCellButton className="shrink-1 min-w-0 grow">{getLabelUi()}</FilterCellButton>
					</ProjectMultiSelectDropdown>
				)
			}
		}
	}

	return (
		<FilterCellRoot>
			<FilterCellBadge>
				<LuIcon icon={modelIconMapping.project} />
				Project
			</FilterCellBadge>

			<FilterModifierDropdown filterDef={filterDef} filter={filter}>
				<FilterCellButton>
					{filterDef.getFilterModifier(filterModifier).modifier.render(filterValue)}
				</FilterCellButton>
			</FilterModifierDropdown>

			{getFilterValueUi()}

			<RemoveFilterButton filter={filter} filterManager={filterManager} />
		</FilterCellRoot>
	)
}
